import { useCallback, useState } from "react";

import {
  BrandAssetContent,
  CorporateBrandAssetDto,
  CorporateBrandAssetRequest,
  LogoType,
} from "../types";
import {
  EMPTY_BRAND_ASSET_CONTENT,
  GLOBAL_CORPORATE_ID,
  PARAM_LOGO_HEIGHT_KEY,
  validateColorVariables,
} from "../constants";
import {
  InvalidBrandAssetZipError,
  buildFileZip,
  extractBrandAssetContent,
  parseBrandAssetZip,
} from "../services";

interface UseBrandAssetEditorParams {
  corporateId: number;
  corporateName: string;
  globalCorporateName: string;
  createBrandAsset: (body: CorporateBrandAssetRequest) => Promise<unknown>;
  editBrandAsset: (
    id: number,
    body: CorporateBrandAssetRequest,
  ) => Promise<unknown>;
}

export const useBrandAssetEditor = ({
  corporateId,
  corporateName,
  globalCorporateName,
  createBrandAsset,
  editBrandAsset,
}: UseBrandAssetEditorParams) => {
  const [name, setName] = useState("");
  const [content, setContent] = useState<BrandAssetContent>(
    EMPTY_BRAND_ASSET_CONTENT,
  );
  const [originalContent, setOriginalContent] = useState<BrandAssetContent>(
    EMPTY_BRAND_ASSET_CONTENT,
  );
  const [loadingSource, setLoadingSource] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const loadFrom = useCallback(
    async (source: CorporateBrandAssetDto | null) => {
      if (!source) {
        setContent(EMPTY_BRAND_ASSET_CONTENT);
        setOriginalContent(EMPTY_BRAND_ASSET_CONTENT);
        return;
      }
      setLoadingSource(true);
      try {
        const loaded = await extractBrandAssetContent(source);
        setContent(loaded);
        setOriginalContent(loaded);
      } finally {
        setLoadingSource(false);
      }
    },
    [],
  );

  const loadFromZipFile = useCallback(async (file: File) => {
    setLoadingSource(true);
    try {
      const loaded = await parseBrandAssetZip(file);

      const validation = validateColorVariables(loaded.colors);
      if (!validation.valid)
        throw new InvalidBrandAssetZipError(validation.missing);

      setContent(loaded);
      setOriginalContent(loaded);
      return loaded;
    } finally {
      setLoadingSource(false);
    }
  }, []);

  const setColor = useCallback((key: string, value: string) => {
    setContent((c) => ({ ...c, colors: { ...c.colors, [key]: value } }));
  }, []);

  const removeColor = useCallback((key: string) => {
    setContent((c) => {
      const { [key]: _removed, ...rest } = c.colors;
      return { ...c, colors: rest };
    });
  }, []);

  const setLogo = useCallback((type: LogoType, dataUrl: string) => {
    setContent((c) => ({ ...c, logos: { ...c.logos, [type]: dataUrl } }));
  }, []);

  const setParameter = useCallback((key: string, value: unknown) => {
    setContent((c) => ({
      ...c,
      parameters: { ...c.parameters, [key]: value },
    }));
  }, []);

  const setLogoHeight = useCallback(
    (height: number) => {
      setParameter(PARAM_LOGO_HEIGHT_KEY, height);
    },
    [setParameter],
  );

  const buildRequest = useCallback(
    async (
      targetCorporateId: number,
      prefixName: string,
    ): Promise<CorporateBrandAssetRequest> => ({
      corporateId: targetCorporateId,
      name: name.trim(),
      fileZip: await buildFileZip(prefixName, content),
    }),
    [name, content],
  );

  const save = useCallback(
    async (editingId?: number) => {
      setSubmitting(true);
      try {
        const body = await buildRequest(corporateId, corporateName);
        if (editingId) await editBrandAsset(editingId, body);
        else await createBrandAsset(body);
      } finally {
        setSubmitting(false);
      }
    },
    [
      buildRequest,
      corporateId,
      corporateName,
      createBrandAsset,
      editBrandAsset,
    ],
  );

  const saveToGlobal = useCallback(async () => {
    setSubmitting(true);
    try {
      const body = await buildRequest(GLOBAL_CORPORATE_ID, globalCorporateName);
      await createBrandAsset(body);
    } finally {
      setSubmitting(false);
    }
  }, [buildRequest, globalCorporateName, createBrandAsset]);

  return {
    name,
    setName,
    content,
    originalContent,
    setColor,
    removeColor,
    setLogo,
    setParameter,
    setLogoHeight,
    loadFrom,
    loadFromZipFile,
    loadingSource,
    submitting,
    save,
    saveToGlobal,
  };
};
